import {
  CreateOrderForDeliveryDestinationPayload,
  CreateOrderForDestinationPayload,
  CreateOrderPayload,
} from 'src/app/core/actions/order.actions';
import { Address } from './address';
import { Booking } from './booking';
import { LineItem } from './line-item';
import { Money, MoneyAttributes } from './money';
import { Storefront } from 'src/app/core/storefront.service';
import { DeliveryAddress } from './custom-destination';

interface OrderPromotion {
  name: string | null;
  state: string | null;
  description: string | null;
}


export interface OrderCondition {
  id: number;
  order_id: number;
  condition: string;
  response: string;
}


export interface Order {
  id: number;
  bookings: Booking[];
  line_items: LineItem[];
  number: string;
  state: string;
  user_id: number | null;
  bill_address_id: number | null;
  email: string | null;
  phone: string | null;
  item_count: number;
  token: string;
  address_required: boolean;
  customer_name_required: boolean;
  display_as_zero: boolean;
  promotion: OrderPromotion | null;
  customer_name: string | null;
  payment_method: string | null;
  card_last_four: string | null;
  card_type: string | null;
  card_exp_month: string | null;
  card_exp_year: string | null;
  total_cents: number;
  total?: MoneyAttributes;
  item_total_cents: number;
  item_total?: MoneyAttributes;
  gratuity_total_cents: number;
  gratuity_total?: MoneyAttributes;
  processing_fee_total_cents: number;
  processing_fee_total?: MoneyAttributes;
  sales_tax_total_cents: number;
  sales_tax_total?: MoneyAttributes;
  discount_total_cents: number;
  discount_total?: MoneyAttributes;
  discounted_item_total_cents: number;
  discounted_item_total?: MoneyAttributes;
  custom_fee_total_cents: number;
  custom_fee_total?: MoneyAttributes;
  tip_total_cents: number;
  tip_total?: MoneyAttributes;
  promotion_discount_total_cents: number;
  promotion_discount_total?: MoneyAttributes;
  due_total?: MoneyAttributes;
  paid_total?: MoneyAttributes;
  tip?: MoneyAttributes;
  tip_percentage: number;
  tip_amount?: MoneyAttributes;
  billing_address: Address | null;
  notes: string | null;
  custom_fee: number | null;
  order_conditions: OrderCondition[];
  layout_position_identifiers: any[];
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  fulfilled_at: string | null;
  deleted_at: string | null;
  delivery: boolean;
  destination_id: number;
  delivery_address: DeliveryAddress | null;
  checks: any[];
}


export const lineItems = (order: Order | null): LineItem[] => {
  if (!order || !order.line_items) {
    return [];
  }
  
  const groupedItems = order.line_items
    .reduce((acc, item) => {
      const productId = item.product_id;
      if (!acc[productId]) {
        acc[productId] = [];
      }
      acc[productId].push(item);
      return acc;
    }, {} as { [key: number]: LineItem[] });

  return Object
    .values(groupedItems)
    .flatMap(row => {
        const line_item_ids = row.map(item => item.id);
        return ({ 
          ...row[0], 
          line_item_ids, 
          quantity: row.length 
        }) as LineItem;
    });
}

export const hasItems = (order: Order | null): boolean =>{
  if (!order) {
    return false;
  }
  return totalNumberOfItems(order) > 0;
}

export const hasActivePromotion = (order: Order | null): boolean =>{
  if (!order) {
    return false;
  }
  if (!order.promotion_discount_total) {
    return order.promotion_discount_total_cents < 0;
  }

  return (order.promotion_discount_total.cents ?? 0) < 0;
}

export const hasDiscount = (order: Order | null): boolean =>{
  if (!order) {
    return false;
  }
  return order.discount_total_cents !== 0;
}

export const numberOfBookings = (order: Order | null): number =>{
  if (!order || !order.bookings) {
    return 0;
  }
  return order.bookings.length;
}

export const numberOfLineItems = (order: Order | null): number => lineItems(order)
  .reduce((acc, curr) => {
    acc += curr.quantity ?? 0;
    return acc;
  }, 0);

export const totalNumberOfItems = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return numberOfBookings(order) + numberOfLineItems(order);
}

export const discountTotal = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return order.discount_total_cents / 100;
}

export const discountedItemTotal = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return order.discounted_item_total_cents / 100;
}

export const itemTotal = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return order.item_total_cents / 100;
}

export const orderTotal = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return order.total_cents / 100;
}

export const orderTip = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return order.tip_total_cents / 100;
}

export const taxesAndOtherFees = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  return (order.processing_fee_total_cents + order.sales_tax_total_cents + order.custom_fee_total_cents) / 100;
}

export const automaticGratuityTotal = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }

  if (!order.gratuity_total) {
    if (order.gratuity_total_cents > 0) {
      return order.gratuity_total_cents / 100;
    }

    return 0;
  }

  return new Money(order.gratuity_total).unit;
}

export const promotionDiscountTotal = (order: Order | null): number =>{
  if (!order) {
    return 0;
  }
  if (!order.promotion_discount_total) {
    if (order.promotion_discount_total_cents !== null && order.promotion_discount_total_cents !== undefined) {
      return order.promotion_discount_total_cents / 100;
    }

    return 0;
  }

  return new Money(order.promotion_discount_total).unit;
}

export const deliveryAddressRepresentation = (order: Order | null): string =>{
  if (!order) {
    return '';
  }
  if (!order.delivery || !order.delivery_address) {
    return '';
  }

  return `${
    order.delivery_address.address1}, ${
    order.delivery_address.city}, ${
    order.delivery_address.zip_code} ${
    order.delivery_address.state_name}`;
}


export const createOrderPayload = (args: {order: Order, storefront: Storefront | null}): CreateOrderPayload => {
  const { order, storefront } = args;
  let payload: CreateOrderPayload = new CreateOrderForDestinationPayload({
    destinationId: order.destination_id,
    slug: storefront?.slug || '',
  });

  if (order.delivery && !!order.delivery_address) {
    payload = new CreateOrderForDeliveryDestinationPayload({
      warehouseId: order.destination_id,
      address: order.delivery_address,
      slug: storefront?.slug || '',
    });
  }

  return payload;
}

export const orderItemsToAnalytics = (order: Order) => [...(order.bookings || []), ...(order.line_items || [])]
    .map((item: LineItem | Booking) => `{ product_id: ${item.product_id}, id: ${item.id} }`);
